import React from 'react'
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'
import useStayScrolled from 'react-stay-scrolled'

import T from 'components/FormattedMessage'
import CenteredBottom from 'components/CenteredBottom'

import { Grid, Typography, RootRef } from '@material-ui/core'
import { withStyles, WithStyles, createStyles, Theme, WithTheme } from '@material-ui/core/styles'

import { ILeaderboardEntry } from 'protos'

import inactiveChatIconUrl from './assets/chat_inactive.svg'
import GameChatMessage from './components/GameChatMessage'
import GameChatInput from './components/GameChatInput'
import GameChatBar from './components/GameChatBar'
import { GameChatComment } from './'

interface Props {
  active?: boolean
  ownUserId: string
  leaderboard: ILeaderboardEntry[]
  comments: GameChatComment[]
  onSend: (text: string) => void
}

const styles = (theme: Theme) => createStyles({
  root: {
    backgroundColor: theme.palette.common.white,
    overflow: 'hidden',
    transition: 'height 0.5s',
    boxShadow: '0 -7px 7px -7px rgba(0, 0, 0, 0.25)',
  },
  emptyIcon: {
    width: 48,
    height: 48,
    display: 'block',
    margin: `${theme.spacing() * 12}px auto ${theme.spacing() * 2}px`,
  },
  commentContainer: {
    overflowY: 'auto',
  },
  comments: {
    backgroundColor: theme.palette.common.white,
  },
})

type InjectedProps = Props & WithStyles<typeof styles> & WithTheme

const GameChat: React.FC<InjectedProps> = ({
  classes,
  active,
  comments,
  leaderboard,
  ownUserId,
  onSend,
  theme,
}) => {
  const { headerMinHeight, headerMaxHeight } = theme.components.GameLayout
  const [open, setOpen] = React.useState(false)
  const [chatHeight, setChatHeight] = React.useState(window.innerHeight - headerMinHeight)
  const [oldScrollTop, setOldScrollTop] = React.useState(0)
  const commentsRef = React.useRef<HTMLDivElement>(null)
  const { stayScrolled } = useStayScrolled(commentsRef)
  const [text, setText] = React.useState('')
  const rootRef = React.useRef<HTMLDivElement>(null)

  const send = () => {
    onSend(text)
    setText('')
  }

  const mention = (name: string) => {
    setText(`${text} @${name}`)
  }

  const toggleChat = () => {
    setOpen(!open)

    const scrollTo = (y: number) => {
      window.scrollTo(window.scrollX, y)
    }

    const rootEl = rootRef.current!

    if (!open) {
      const currentScrollTop = Math.max(
        0,
        window.scrollY ||
        window.pageYOffset ||
        document.body.scrollTop + (document.documentElement?.scrollTop ?? 0),
      )

      setOldScrollTop(currentScrollTop)
      commentsRef.current!.scrollTop = commentsRef.current!.scrollHeight

      scrollTo(Math.max(currentScrollTop, headerMaxHeight - headerMinHeight))
      disableBodyScroll(rootEl)
    } else {
      enableBodyScroll(rootEl)
      scrollTo(oldScrollTop)
    }
  }

  React.useEffect(() => {
    const onResize = () => {
      setChatHeight(window.innerHeight - headerMinHeight)
    }

    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      clearAllBodyScrollLocks()
    }
  }, [headerMinHeight])

  React.useLayoutEffect(() => {
    stayScrolled()
  }, [comments.length, stayScrolled])

  return (
    <CenteredBottom fillHeight={theme.components.GameChatBar.height - 1}>
      <div
        ref={rootRef}
        className={classes.root}
        style={{ height: !open ? theme.components.GameChatBar.height : chatHeight }}
      >
        <Grid container direction="column" style={{ height: chatHeight }}>
          <GameChatBar
            open={open}
            active={active}
            onClick={toggleChat}
            numComments={comments.length}
          />
          <RootRef rootRef={commentsRef}>
            <Grid item xs className={classes.commentContainer}>
              {comments.length === 0 && (
                <>
                  <img alt="" src={inactiveChatIconUrl} className={classes.emptyIcon} />
                  <Typography variant="body2" align="center" color="textSecondary">
                    <T id="chat_empty_message" />
                  </Typography>
                </>
              )}
              <div className={classes.comments}>
                {comments.map(({ from, msg, ts }, i) => {
                  const entry = leaderboard.find(({ user }) => Boolean(user && user.userId! === from))
                  if (!entry || !entry.user) {
                    return null
                  }

                  const userName = entry.user.name!

                  return (
                    <GameChatMessage
                      key={i}
                      incoming={from !== ownUserId}
                      user={entry.user!}
                      timestamp={ts}
                      message={msg}
                      onMention={() => mention(userName)}
                    />
                  )
                })}
              </div>
            </Grid>
          </RootRef>
          <GameChatInput text={text} setText={setText} onSend={send} />
        </Grid>
      </div>
    </CenteredBottom>
  )
}

export default withStyles(styles, { withTheme: true })(GameChat)
